import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
  Signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { DialogShellComponent } from '@custom-components/overlay/dialog-shell/dialog-shell.component';
import { DeviceStore } from '@root/app/store/device.store';
import {
  VerificationAttempt,
  VerificationChannelData,
  VerificationDetails,
} from '@core/models/verification.model';
import { SpinnerComponent } from '@custom-components/general/spinner/spinner.component';
import {
  StatusBadgeType,
  StatusChipComponent,
} from '@custom-components/general/status-chip/status-chip.component';

@Component({
  selector: 'xemex-device-verification-details-dialog',
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatRadioModule,
    FormsModule,
    DialogShellComponent,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    TranslateModule,
    SpinnerComponent,
    StatusChipComponent,
  ],
  providers: [TranslatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './device-verification-details-dialog.component.html',
})
export class DeviceVerificationDetailsDialogComponent {
  readonly deviceStore = inject(DeviceStore);
  readonly dialogRef = inject(MatDialogRef<DeviceVerificationDetailsDialogComponent>);
  public data = inject<VerificationAttempt>(MAT_DIALOG_DATA);
  verificationDetails: Signal<VerificationDetails | undefined> = signal(undefined);
  verificationId: string;
  private pollTimeout: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this.pollTimeout));
    this.verificationId = this.data.verificationId;
    if (this.verificationId) {
      this.deviceStore.getVerificationDetails(this.verificationId);
      effect(() => {
        if (this.verificationId == this.deviceStore.verificationDetails()?.verificationId) {
          this.verificationDetails = this.deviceStore.verificationDetails;
          const status = this.verificationDetails()?.status;
          if (status != 'Success' && status != 'Failed') {
            this.schedulePoll();
          }
        }
      });
    }
  }

  // A DLMS verification runs for as long as the executor's read budget allows (180 s), where the
  // Lewiz direct method is bounded at ~30 s. Re-requesting the moment each non-terminal response
  // lands would therefore hammer the endpoint for minutes instead of seconds, so the retry waits.
  private schedulePoll(): void {
    clearTimeout(this.pollTimeout);
    this.pollTimeout = setTimeout(
      () => this.deviceStore.getVerificationDetails(this.verificationId),
      POLL_INTERVAL_MS,
    );
  }

  // A verification that read nothing at all otherwise renders as a blank dialog, indistinguishable
  // from one still loading. It deliberately does not claim *why* nothing came back: the device may
  // have no RS-485 subdevices registered, or none of its registers may be known.
  hasFinishedWithoutResults(): boolean {
    const details = this.verificationDetails();
    if (details === undefined) return false;
    if (details.status !== 'Success' && details.status !== 'Failed') return false;
    return (details.results?.channels ?? []).length === 0;
  }

  wasRead(register: VerificationChannelData): boolean {
    return register.readStatus === null || register.readStatus === 'Success';
  }

  readStatusChip(readStatus: string): StatusBadgeType {
    if (readStatus === 'Success') return 'success';
    if (readStatus === 'Failed') return 'error';
    // NotRead means the read budget ran out before this register's turn, which is not a device
    // fault, so it does not deserve the same weight as a subdevice that failed to answer.
    return 'warning';
  }

  readStatusLabel(readStatus: string): string {
    return READ_STATUS_TRANSLATION_KEYS[readStatus] ?? readStatus;
  }

  ageLabel(register: VerificationChannelData): string | null {
    const seconds = register.ageInSeconds;
    if (seconds === null || seconds === undefined) return null;
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    return `${Math.floor(minutes / 60)}h`;
  }
}

export const POLL_INTERVAL_MS = 3000;

// Falls back to the raw value for an unrecognised status so a new executor status still renders as
// something rather than as a missing translation key.
const READ_STATUS_TRANSLATION_KEYS: Record<string, string> = {
  Success: 'devices.detailTabs.verification.details.readStatus.success',
  Failed: 'devices.detailTabs.verification.details.readStatus.failed',
  NotRead: 'devices.detailTabs.verification.details.readStatus.notRead',
};
